import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { Camera, CameraOff, AlertTriangle, Eye, Users, UserX, Loader2 } from 'lucide-react';

const AIProctoringCamera = ({ 
  enabled = true,
  onViolation,
  onStatusChange,
  violationThreshold = 10,
  warningThreshold = 5,
  captureOnViolation = true,
  attemptId,
  challengeId
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);
  
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('initializing');
  const [violations, setViolations] = useState([]);
  const [totalViolations, setTotalViolations] = useState(0);
  const [lastDetection, setLastDetection] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setCurrentStatus('loading_models');
        const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model/';
        
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
          faceapi.nets.faceExpressionNet.loadFromUri(MODEL_URL)
        ]);
        
        setModelsLoaded(true);
        setCurrentStatus('models_loaded');
        console.log('Face-api models loaded successfully');
      } catch (error) {
        console.error('Error loading face-api models:', error);
        setCurrentStatus('model_error');
      }
    };

    if (enabled) {
      loadModels();
    }

    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
      stopCamera();
    };
  }, [enabled]);

  // Start camera after models are loaded
  useEffect(() => {
    if (modelsLoaded && enabled) {
      startCamera();
    }
  }, [modelsLoaded, enabled]);

  const startCamera = async () => {
    try {
      setCurrentStatus('requesting_camera');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraActive(true);
          setCurrentStatus('active');
          startDetection();
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCurrentStatus('camera_error');
      handleViolation('camera_blocked', 'Camera access denied or blocked');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }
    setCameraActive(false);
  };

  const startDetection = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
    }

    detectionIntervalRef.current = setInterval(async () => {
      if (videoRef.current && cameraActive) {
        await detectFaces();
      }
    }, 500); // Run detection every 500ms
  };

  const detectFaces = async () => {
    if (!videoRef.current || !modelsLoaded) return;

    try {
      const detections = await faceapi
        .detectAllFaces(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks(true);

      setLastDetection({
        timestamp: new Date().toISOString(),
        faceCount: detections.length,
        detections: detections
      });

      // Draw detections on canvas
      if (canvasRef.current && videoRef.current) {
        const displaySize = {
          width: videoRef.current.videoWidth,
          height: videoRef.current.videoHeight
        };
        faceapi.matchDimensions(canvasRef.current, displaySize);
        
        const resizedDetections = faceapi.resizeResults(detections, displaySize);
        
        const ctx = canvasRef.current.getContext('2d');
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        
        // Draw face boxes
        resizedDetections.forEach((detection, i) => {
          const box = detection.detection.box;
          ctx.strokeStyle = detections.length === 1 ? '#00ff00' : '#ff0000';
          ctx.lineWidth = 2;
          ctx.strokeRect(box.x, box.y, box.width, box.height);
          
          // Draw landmarks for eye tracking
          if (detection.landmarks) {
            const leftEye = detection.landmarks.getLeftEye();
            const rightEye = detection.landmarks.getRightEye();
            
            ctx.fillStyle = '#00ffff';
            [...leftEye, ...rightEye].forEach(point => {
              ctx.beginPath();
              ctx.arc(point.x, point.y, 2, 0, 2 * Math.PI);
              ctx.fill();
            });
          }
        });
      }

      // Check for violations
      if (detections.length === 0) {
        handleViolation('no_face', 'No face detected in camera');
      } else if (detections.length > 1) {
        handleViolation('multiple_faces', `Multiple faces detected (${detections.length})`);
      } else {
        // Check eye gaze (basic implementation using face landmarks)
        const face = detections[0];
        if (face.landmarks) {
          const isLookingAway = checkGazeDirection(face.landmarks);
          if (isLookingAway) {
            handleViolation('looking_away', 'Looking away from screen detected');
          } else {
            // Clear any active warning if everything is normal
            if (showWarning) {
              setShowWarning(false);
            }
          }
        }
      }
    } catch (error) {
      console.error('Detection error:', error);
    }
  };

  const checkGazeDirection = (landmarks) => {
    try {
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();
      const nose = landmarks.getNose();
      
      // Calculate eye center
      const leftEyeCenter = {
        x: leftEye.reduce((sum, p) => sum + p.x, 0) / leftEye.length,
        y: leftEye.reduce((sum, p) => sum + p.y, 0) / leftEye.length
      };
      const rightEyeCenter = {
        x: rightEye.reduce((sum, p) => sum + p.x, 0) / rightEye.length,
        y: rightEye.reduce((sum, p) => sum + p.y, 0) / rightEye.length
      };
      
      // Calculate nose tip position
      const noseTip = nose[nose.length - 1];
      
      // Calculate face center (between eyes)
      const faceCenter = {
        x: (leftEyeCenter.x + rightEyeCenter.x) / 2,
        y: (leftEyeCenter.y + rightEyeCenter.y) / 2
      };
      
      // Check if nose is significantly off-center (looking away)
      const horizontalOffset = Math.abs(noseTip.x - faceCenter.x);
      const eyeDistance = Math.abs(rightEyeCenter.x - leftEyeCenter.x);
      
      // If nose offset is more than 40% of eye distance, consider looking away
      return horizontalOffset > eyeDistance * 0.4;
    } catch (error) {
      return false;
    }
  };

  const handleViolation = useCallback((type, message) => {
    const violation = {
      type,
      message,
      timestamp: new Date().toISOString(),
      attemptId,
      challengeId
    };

    setViolations(prev => [...prev, violation]);
    setTotalViolations(prev => prev + 1);
    
    // Show warning
    setWarningMessage(message);
    setShowWarning(true);
    
    // Auto-hide warning after 3 seconds
    setTimeout(() => {
      setShowWarning(false);
    }, 3000);

    // Capture screenshot if enabled
    if (captureOnViolation && videoRef.current) {
      captureScreenshot(violation);
    }

    // Notify parent component
    if (onViolation) {
      onViolation(violation);
    }

    // Update status
    if (onStatusChange) {
      onStatusChange({
        totalViolations: totalViolations + 1,
        lastViolation: violation,
        isTerminated: totalViolations + 1 >= violationThreshold
      });
    }
  }, [attemptId, challengeId, captureOnViolation, onViolation, onStatusChange, totalViolations, violationThreshold]);

  const captureScreenshot = async (violation) => {
    if (!videoRef.current) return;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoRef.current, 0, 0);
      
      const screenshot = canvas.toDataURL('image/jpeg', 0.7);
      
      // Store screenshot with violation data
      violation.screenshot = screenshot;
      
      // You could send this to backend here
      console.log('Screenshot captured for violation:', violation.type);
    } catch (error) {
      console.error('Error capturing screenshot:', error);
    }
  };

  const getStatusColor = () => {
    switch (currentStatus) {
      case 'active': return 'bg-green-500';
      case 'camera_error':
      case 'model_error': return 'bg-red-500';
      default: return 'bg-amber-500';
    }
  };

  const getStatusText = () => {
    switch (currentStatus) {
      case 'initializing': return 'Initializing...';
      case 'loading_models': return 'Loading AI models...';
      case 'models_loaded': return 'Models ready';
      case 'requesting_camera': return 'Requesting camera...';
      case 'active': return 'Proctoring Active';
      case 'camera_error': return 'Camera Error';
      case 'model_error': return 'Model Error';
      default: return 'Unknown';
    }
  };

  if (!enabled) {
    return null;
  }

  return (
    <div className="relative">
      {/* Camera Feed */}
      <div className="relative w-48 h-36 bg-black rounded-lg overflow-hidden border-2 border-white/20">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          autoPlay
          playsInline
          muted
        />
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full"
        />
        
        {/* Loading overlay */}
        {!cameraActive && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
            <div className="text-center">
              <Loader2 className="animate-spin text-white mx-auto mb-2" size={24} />
              <p className="text-white text-xs">{getStatusText()}</p>
            </div>
          </div>
        )}

        {/* Status indicator */}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <div className={`w-2 h-2 rounded-full ${getStatusColor()} animate-pulse`} />
          <span className="text-xs text-white bg-black/50 px-1 rounded">
            {cameraActive ? 'LIVE' : 'OFF'}
          </span>
        </div>

        {/* Violation counter */}
        <div className="absolute top-2 right-2">
          <div className={`px-2 py-0.5 rounded text-xs font-medium ${
            totalViolations >= warningThreshold 
              ? 'bg-red-500 text-white' 
              : 'bg-white/20 text-white'
          }`}>
            {totalViolations}/{violationThreshold}
          </div>
        </div>

        {/* Camera icon */}
        <div className="absolute bottom-2 right-2">
          {cameraActive ? (
            <Camera className="text-green-400" size={16} />
          ) : (
            <CameraOff className="text-red-400" size={16} />
          )}
        </div>
      </div>

      {/* Warning Banner */}
      {showWarning && (
        <div className="absolute -bottom-16 left-0 right-0 bg-red-500 text-white p-2 rounded-lg flex items-center gap-2 animate-pulse shadow-lg">
          <AlertTriangle size={16} />
          <span className="text-xs font-medium flex-1">{warningMessage}</span>
        </div>
      )}

      {/* Detection Status */}
      <div className="mt-2 space-y-1">
        <div className="flex items-center gap-2 text-xs">
          <Eye size={12} className={lastDetection?.faceCount === 1 ? 'text-green-400' : 'text-gray-500'} />
          <span className={lastDetection?.faceCount === 1 ? 'text-green-400' : 'text-gray-500'}>
            Face: {lastDetection?.faceCount === 1 ? 'Detected' : 'Not detected'}
          </span>
        </div>
        {lastDetection?.faceCount > 1 && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <Users size={12} />
            <span>Multiple faces: {lastDetection.faceCount}</span>
          </div>
        )}
        {lastDetection?.faceCount === 0 && (
          <div className="flex items-center gap-2 text-xs text-amber-400">
            <UserX size={12} />
            <span>No face in frame</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIProctoringCamera;
